import React from 'react'

import {
    Popover as GluestackPopover,
    PopoverContent as GluestackPopoverContent,
} from '../../_gluestack/popover'
import { useDismissOnOutsideScroll } from '../../useDismissOnOutsideScroll'

type GluestackPopoverProps = React.ComponentProps<typeof GluestackPopover>
type GluestackPopoverContentProps = React.ComponentProps<typeof GluestackPopoverContent>
type TriggerRenderer = GluestackPopoverProps['trigger']
type TriggerProps = Parameters<TriggerRenderer>[0] & { ref?: React.Ref<unknown> }

interface PopoverDismissContextValue {
    isOpen: boolean
    onClose?: () => void
    triggerRef: React.MutableRefObject<unknown>
}

const noTrigger: React.MutableRefObject<unknown> = { current: null }

const PopoverDismissContext = React.createContext<PopoverDismissContextValue>({
    isOpen: false,
    triggerRef: noTrigger,
})

/**
 * The provider sits inside `children`, not around the Gluestack popover: the
 * overlay hoists the children element through `PortalProvider` state, so a
 * provider outside it is not an ancestor of the panel at render time.
 */
export const Popover = React.forwardRef<
    React.ComponentRef<typeof GluestackPopover>,
    GluestackPopoverProps
>(function Popover({ isOpen, onClose, trigger, children, ...props }, ref) {
    const triggerRef = React.useRef<unknown>(null)
    const dismiss = React.useMemo(
        () => ({ isOpen: !!isOpen, onClose, triggerRef }),
        [isOpen, onClose],
    )

    const renderTrigger = React.useCallback<TriggerRenderer>(
        (triggerProps, state) => {
            const { ref: gluestackRef, ...rest } = triggerProps as TriggerProps
            const setNode = (node: unknown) => {
                triggerRef.current = node
                if (typeof gluestackRef === 'function') {
                    gluestackRef(node as never)
                } else if (gluestackRef) {
                    ;(gluestackRef as React.MutableRefObject<unknown>).current = node
                }
            }
            return trigger({ ...rest, ref: setNode } as TriggerProps, state)
        },
        [trigger],
    )

    return (
        <GluestackPopover
            ref={ref}
            isOpen={isOpen}
            onClose={onClose}
            trigger={renderTrigger}
            {...props}
        >
            <PopoverDismissContext.Provider value={dismiss}>
                {children}
            </PopoverDismissContext.Provider>
        </GluestackPopover>
    )
})

Popover.displayName = 'Popover'

export const PopoverContent = React.forwardRef<
    React.ComponentRef<typeof GluestackPopoverContent>,
    GluestackPopoverContentProps
>(function PopoverContent(props, ref) {
    const { isOpen, onClose, triggerRef } = React.useContext(PopoverDismissContext)
    useDismissOnOutsideScroll(isOpen, triggerRef, onClose)

    return <GluestackPopoverContent ref={ref} {...props} />
})

PopoverContent.displayName = 'PopoverContent'
